import React, { createContext, useContext, useState, useEffect } from 'react';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState(() => {
    const savedCart = localStorage.getItem('cart');
    return savedCart ? JSON.parse(savedCart) : [];
  });

  const [isCartOpen, setIsCartOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify(cartItems));
  }, [cartItems]);

  const addToCart = (product, quantity = 1, options = null) => {
    setCartItems(prevItems => {
      // Create a unique identifier based on product ID, color, AND handle
      const itemIdentifier = options?.colorId 
        ? `${product.id}-${options.colorId}-${options.handle || 'no-handle'}` 
        : product.id;
      
      // Find existing item with same product ID, color, AND handle
      const existingItem = prevItems.find(item => {
        const existingIdentifier = item.options?.colorId 
          ? `${item.id}-${item.options.colorId}-${item.options.handle || 'no-handle'}` 
          : item.id;
        return existingIdentifier === itemIdentifier;
      });
      
      if (existingItem) {
        // Update quantity of existing item with same color and handle
        return prevItems.map(item => {
          const existingIdentifier = item.options?.colorId 
            ? `${item.id}-${item.options.colorId}-${item.options.handle || 'no-handle'}` 
            : item.id;
          return existingIdentifier === itemIdentifier
            ? { ...item, quantity: item.quantity + quantity }
            : item;
        });
      }
      
      // Add new item with options (color and handle info)
      return [...prevItems, { 
        ...product, 
        quantity,
        options: options || null,
        // Store a unique cart item ID for proper removal
        cartItemId: itemIdentifier
      }];
    });
    setIsCartOpen(true);
  };

  const removeFromCart = (cartItemId) => {
    setCartItems(prevItems => prevItems.filter(item => {
      // Use cartItemId if it exists, otherwise fall back to item.id
      const itemId = item.cartItemId || item.id;
      return itemId !== cartItemId;
    }));
  };

  const updateQuantity = (cartItemId, quantity) => {
    if (quantity <= 0) {
      removeFromCart(cartItemId);
      return;
    }
    
    setCartItems(prevItems =>
      prevItems.map(item => {
        // Use cartItemId if it exists, otherwise fall back to item.id
        const itemId = item.cartItemId || item.id;
        return itemId === cartItemId
          ? { ...item, quantity } 
          : item;
      })
    );
  };

  const clearCart = () => {
    setCartItems([]);
  };

  const getCartTotal = (region) => {
    return cartItems.reduce((total, item) => {
      const price = region === 'US' ? item.priceUSD : item.priceCAD;
      return total + (price * item.quantity);
    }, 0);
  };

  const getCartCount = () => {
    return cartItems.reduce((count, item) => count + item.quantity, 0);
  };

  const value = {
    cartItems,
    isCartOpen,
    setIsCartOpen,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    getCartTotal,
    getCartCount,
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};